'use client'

import React, { useState, useEffect } from "react";
import { Check, X } from "@phosphor-icons/react";
import TextInputField from "@/components/ui/TextInputField/TextInputField";
import DropdownField from "@/components/ui/DropdownField/DropdownField";
import Headline from "@/components/ui/Headline/Headline";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import { SubmitFormButton } from "@/components/ui/SubmitFormButton";
import { LanguageType, LanguageDirection } from "@/lib/types";
import Image from "next/image";
import {
    createEmailChangeHandler,
    createPhoneChangeHandler,
    createRequiredFieldChangeHandler,
    createDropdownChangeHandler,
    submitToZoho,
    checkAllMandatoryFields,
    checkFormValidationErrors,
    VALIDATION_HINTS,
    VALIDATION_HINTS_AR
} from "@/lib/formUtils";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";
import { lead, newMetaEventId } from "@/lib/gtm";
import { useIsLg, useIsMidxl2 } from '@/contexts/BreakpointContext';

interface LPFormProps {
    language?: LanguageType;
    headlineText: string;
    subheadingText: string;
    benefits?: string[];
    onSubmit?: (formData: FormData) => void;
    borderColor?: string;
    shadowClass?: string;
    useAlternativeLayout?: boolean; // New optional parameter
    innerHorizontalPadding?: string;
    innerVerticalPadding?: string;
    onClose?: () => void; // Added onClose prop
    shouldPreventLenisBehavior?: boolean; // Default to false to allow lenis behavior
}

interface FormData {
    First_Name: string;
    Last_Name: string;
    Email: string;
    Phone_number: string;
    Preferred_Contact_Time: string;
    Preferred_Language: string;
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
    english: "ltr",
    arabic: "rtl"
};

const LPForm: React.FC<LPFormProps> = ({
    language = "english",
    headlineText,
    subheadingText,
    benefits = [],
    onSubmit,
    borderColor = "border-stroke-soft-200",
    shadowClass = "",
    innerHorizontalPadding = "px-0",
    innerVerticalPadding = "py-0",
    useAlternativeLayout = false, // Default to false to show current layout
    onClose, // Destructure onClose prop
    shouldPreventLenisBehavior = false, // Default to false to allow lenis behavior
}) => {
    const isLg = useIsLg();
    const isMidxl2 = useIsMidxl2();
    const animatedNavigate = useAnimatedNavigate();

    // Form state
    const [formData, setFormData] = useState<FormData>({
        First_Name: "",
        Last_Name: "",
        Email: "",
        Phone_number: "",
        Preferred_Contact_Time: "",
        Preferred_Language: ""
    });

    // Validation states
    const [isEmailInvalid, setIsEmailInvalid] = useState(false);
    const [isPhoneInvalid, setIsPhoneInvalid] = useState(false);
    const [isFirstNameInvalid, setIsFirstNameInvalid] = useState(false);
    const [isLastNameInvalid, setIsLastNameInvalid] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    // Create handlers using utilities
    const handleEmailChange = createEmailChangeHandler(setFormData, setIsEmailInvalid, 'Email');
    const handlePhoneChange = createPhoneChangeHandler(setFormData, setIsPhoneInvalid, 'Phone_number');
    const handleFirstNameChange = createRequiredFieldChangeHandler(setFormData, setIsFirstNameInvalid, 'First_Name');
    const handleLastNameChange = createRequiredFieldChangeHandler(setFormData, setIsLastNameInvalid, 'Last_Name');
    const handleDropdownChange = (field: keyof FormData) => createDropdownChangeHandler(setFormData, field);

    // Check if all mandatory fields have values
    const hasAllMandatoryFields = checkAllMandatoryFields(formData, [
        'Email',
        'Last_Name',
        'First_Name',
        'Phone_number',
        'Preferred_Contact_Time',
        'Preferred_Language'
    ]);

    // Check if form has any validation errors
    const hasValidationErrors = checkFormValidationErrors(
        {
            email: isEmailInvalid,
            firstName: isFirstNameInvalid,
            lastName: isLastNameInvalid,
            phone: isPhoneInvalid
        },
        hasAllMandatoryFields
    );

    // Submit handler
    const handleSubmit = async () => {
        if (hasValidationErrors) return;

        setSubmitStatus('submitting');
        const metaEvent = { event: 'lead' as const, eventId: newMetaEventId('lead') };
        const result = await submitToZoho(formData, 'Website - LP', metaEvent);

        if (result.success) {
            // setSubmitStatus('success');
            lead(formData.Email, formData.Phone_number, metaEvent.eventId);
            onSubmit?.(formData);
            animatedNavigate(result.leadToken ? `/confirmation?t=${encodeURIComponent(result.leadToken)}` : '/confirmation');
        } else {
            setSubmitStatus('error');
        }
    };

    // Reset handler
    const handleResetForm = () => {
        setFormData({
            First_Name: "",
            Last_Name: "",
            Email: "",
            Phone_number: "",
            Preferred_Contact_Time: "",
            Preferred_Language: ""
        });
        setIsEmailInvalid(false);
        setIsPhoneInvalid(false);
        setIsFirstNameInvalid(false);
        setIsLastNameInvalid(false);
        setSubmitStatus('idle');
    };

    // Reset submission status when form data changes
    useEffect(() => {
        if (submitStatus !== 'idle' && submitStatus !== 'submitting') {
            setSubmitStatus('idle');
        }
    }, [formData]);

    return (
        <div
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            {...(shouldPreventLenisBehavior && { 'data-lenis-prevent': true })}
            className={`
                hide-scrollbar
                ${useAlternativeLayout ? 'w-full lg:max-w-[51.6875rem] tb:max-h-[95vh]' : 'w-full lg:max-w-[49.675rem]'}
                bg-bg-light-primary
                border-radius-main
                border-secondary ${borderColor}
                p-[1rem] tb:p-[1rem]
                flex flex-col
                gap-[2rem]
                ${useAlternativeLayout ? 'tb:gap-0' : 'tb:gap-[1rem]'}
                ${shadowClass}
                overflow-y-auto
            `}
        >

            {/* here will come the optional image for the alternative layout */}
            {useAlternativeLayout && (
                <div className="w-full flex justify-center relative tb:flex-1 tb:min-h-[5.25rem] tb:max-h-[13rem]">
                    <Image
                        src="/hero-image.webp"
                        alt="Hero image"
                        width={800}
                        height={400}
                        sizes="(min-width: 768px) 50vw, 100vw"
                        loading="eager"
                        className="
                            w-full h-[7rem] tb:h-full
                            rounded-[16px] tb:rounded-[24px]
                            border-secondary border-primary-base object-cover
                        "
                    />
                    <div className="absolute top-[0.75rem] tb:top-[1.25rem] right-[0.75rem] tb:right-[1.25rem] z-10">
                        <CompactButton
                            size={44}
                            onClick={() => {
                                onClose?.();
                            }}
                        >
                            <X />
                        </CompactButton>
                    </div>
                </div>
            )}


            <div className={`
                flex flex-col tb:flex-shrink-0
                gap-[2.5rem] ${useAlternativeLayout ? 'tb:gap-[2.5rem]' : 'tb:gap-[3.5rem]'}
                ${innerHorizontalPadding}
                ${innerVerticalPadding}
                `}>
                {/* Headline Section */}
                {useAlternativeLayout ? (
                    // Alternative layout: Title, paragraph, and 3-column benefits
                    <div className="w-full flex flex-col gap-[1rem] justify-center items-center">
                        {/* Title */}
                        <h3 className={`text-text-primary text-28--neue-june text-center`}>
                            {headlineText}
                        </h3>

                        {/* Paragraph - invisible on mobile */}
                        <p className="hidden tb:block text-text-tertiary text-18--alexandria text-center">
                            {subheadingText}
                        </p>

                        {/* 3-column benefits layout */}
                        {benefits.length > 0 && isLg && (
                            <div className="w-full flex items-center justify-center">
                                <div
                                    style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                                    className="w-full flex flex-row items-center justify-center gap-[1.5rem]"
                                >
                                    <p className="text-text-tertiary text-14--alexandria text-center py-[0.5rem] min-w-[12rem] w-fit">
                                        {benefits[0]}
                                    </p>

                                    <div className="w-[1.5px] h-[3.5rem] bg-stroke-sub-300"></div>

                                    <p className="text-text-tertiary text-14--alexandria text-center py-[0.5rem] min-w-[12rem] w-fit">
                                        {benefits[1]}
                                    </p>

                                    <div className="w-[1.5px] h-[3.5rem] bg-stroke-sub-300"></div>

                                    <p className="text-text-tertiary text-14--alexandria text-center py-[0.5rem] min-w-[12rem] w-fit">
                                        {benefits[2]}
                                    </p>
                                </div>
                            </div>
                        )}
                        {benefits.length > 0 && !isLg && (
                            <div className="w-full flex flex-col items-center gap-[0.5rem]">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex flex-row items-start justify-center gap-[0.5rem]">
                                        <div className="p-[0.125rem]">
                                            <Check className="text-graphics-secondary" size={16} />
                                        </div>
                                        <p className={`text-text-tertiary text-14--alexandria ${language === 'arabic' ? 'text-right' : 'text-left'}`}>
                                            {benefit}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    // Current layout: Headline component with conditional benefits
                    <div className="w-full flex flex-col gap-[1rem] justify-center items-center">
                        <Headline
                            useH3={true}
                            subheading={subheadingText}
                            heading={headlineText}
                            type="short"
                            alignment="center"
                            language={language}
                            textColor="text-text-primary"
                            headingSize="text-36-to-28--neue-june"
                            shouldAnimate={false}
                        />
                        {!isMidxl2 &&
                            benefits.length > 0 && (
                                <div className="w-full flex flex-col items-center gap-[0.5rem]">
                                    {benefits.map((benefit, index) => (
                                        <div key={index} className="flex flex-row items-start justify-center gap-[0.5rem]">
                                            <div className="p-[0.125rem]">
                                                <Check className="text-graphics-secondary" size={16} />
                                            </div>
                                            <p className={`text-text-tertiary text-14--alexandria ${language === 'arabic' ? 'text-right' : 'text-left'}`}>
                                                {benefit}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                    </div>
                )}


                {/* Form */}

                <div className={`
                    flex flex-col tb:flex-shrink-0
                    gap-[0.25rem] ${useAlternativeLayout ? 'tb:gap-[0.5rem]' : 'tb:gap-[0.75rem]'}
                `}>
                    <div className="w-full">
                        <div className={`grid grid-cols-1 tb:grid-cols-2
                    gap-y-[0.25rem] ${useAlternativeLayout ? 'tb:gap-y-[0.25rem]' : 'tb:gap-y-[0.75rem]'}
                    tb:gap-x-[1rem]
                     `}>
                            {/* Row 1 */}
                            <div style={{ direction: "ltr" }} className="w-full">
                                <TextInputField
                                    language={language}
                                    label={language === 'arabic' ? "الاسم الأول" : "First name"}
                                    size={isLg ? 20 : 18}
                                    value={formData.First_Name}
                                    onChange={handleFirstNameChange}
                                    invalid={isFirstNameInvalid}
                                    placeholder={language === 'arabic' ? "اسمك الأول" : "Your first name"}
                                    hint={isFirstNameInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.FIRST_NAME_REQUIRED : VALIDATION_HINTS.FIRST_NAME_REQUIRED) : undefined}
                                />
                            </div>
                            <div style={{ direction: "ltr" }} className="w-full">
                                <TextInputField
                                    language={language}
                                    label={language === 'arabic' ? "اسم العائلة" : "Last name"}
                                    size={isLg ? 20 : 18}
                                    value={formData.Last_Name}
                                    onChange={handleLastNameChange}
                                    invalid={isLastNameInvalid}
                                    placeholder={language === 'arabic' ? "اسم عائلتك" : "Your last name"}
                                    hint={isLastNameInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.LAST_NAME_REQUIRED : VALIDATION_HINTS.LAST_NAME_REQUIRED) : undefined}
                                />
                            </div>

                            {/* Row 2 */}
                            <div style={{ direction: "ltr" }} className="w-full">
                                <TextInputField
                                    language={language}
                                    label={language === 'arabic' ? "عنوان البريد الإلكتروني" : "Email address"}
                                    size={isLg ? 20 : 18}
                                    value={formData.Email}
                                    onChange={handleEmailChange}
                                    invalid={isEmailInvalid}
                                    placeholder={language === 'arabic' ? "بريدك الإلكتروني للتواصل" : "Your contact email"}
                                    hint={isEmailInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.EMAIL_INVALID : VALIDATION_HINTS.EMAIL_INVALID) : undefined}
                                />
                            </div>
                            <div style={{ direction: "ltr" }} className="w-full">
                                <TextInputField
                                    language={language}
                                    label={language === 'arabic' ? "رقم الهاتف" : "Phone number"}
                                    size={isLg ? 20 : 18}
                                    value={formData.Phone_number}
                                    onChange={handlePhoneChange}
                                    invalid={isPhoneInvalid}
                                    placeholder={language === 'arabic' ? "رقم هاتفك" : "Your phone number"}
                                    hint={isPhoneInvalid ? (language === 'arabic' ? VALIDATION_HINTS_AR.PHONE_INVALID : VALIDATION_HINTS.PHONE_INVALID) : undefined}
                                />
                            </div>

                            {/* Row 3 */}
                            <div style={{ direction: "ltr" }} className="w-full">
                                <DropdownField
                                    size={isLg ? 20 : 18}
                                    language={language}
                                    label={language === 'arabic' ? "وقت التواصل المفضل" : "Preferred contact time"}
                                    placeholder={language === 'arabic' ? "أفضل وقت للاتصال بك" : "Best time for us to call you"}
                                    value={formData.Preferred_Contact_Time}
                                    options={language === 'arabic' ? [
                                        { label: "10 صباحًا – 12 ظهرًا", value: "10 AM - 12 PM" },
                                        { label: "12 ظهرًا – 4 عصرًا", value: "12 PM - 4 PM" },
                                        { label: "4 عصرًا – 8 مساءً", value: "4 PM - 8 PM" },
                                    ] : [
                                        { label: "10 AM – 12 PM", value: "10 AM - 12 PM" },
                                        { label: "12 PM – 4 PM", value: "12 PM - 4 PM" },
                                        { label: "4 PM – 8 PM", value: "4 PM - 8 PM" },
                                    ]}
                                    onChange={handleDropdownChange('Preferred_Contact_Time')}
                                />
                            </div>
                            <div style={{ direction: "ltr" }} className="w-full">
                                <DropdownField
                                    size={isLg ? 20 : 18}
                                    language={language}
                                    label={language === 'arabic' ? "اللغة المفضلة" : "Preferred language"}
                                    placeholder={language === 'arabic' ? "اللغة المفضلة لك" : "Your preferred language"}
                                    value={formData.Preferred_Language}
                                    options={language === 'arabic' ? [
                                        { label: "الإنجليزية", value: "English" },
                                        { label: "العربية", value: "Arabic" },
                                    ] : [
                                        { label: "English", value: "English" },
                                        { label: "Arabic", value: "Arabic" },
                                    ]}
                                    onChange={handleDropdownChange('Preferred_Language')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Button Section */}
                    <SubmitFormButton
                        isLandingPage={true}
                        style="accent"
                        isLg={isLg}
                        onClick={handleSubmit}
                        onReset={handleResetForm}
                        disabled={hasValidationErrors}
                        loading={submitStatus === 'submitting'}
                        language={language}
                        buttonText={
                            submitStatus === 'success'
                                ? (language === 'arabic' ? "تم تأكيد المقعد!" : "Spot secured!")
                                : submitStatus === 'error'
                                    ? (language === 'arabic' ? "لم تتم عملية الإرسال بنجاح" : "Submission unsuccessful")
                                    : (language === 'arabic' ? "الانضمام إلى قائمة الانتظار" : "Join the waitlist")
                        }
                        resetButtonText={language === 'arabic' ? "إعادة تعيين النموذج" : "Reset the form"}
                        privacyText={language === 'arabic' ? "بياناتك محمية. لن نشارك معلوماتك مع أي جهة." : "Your data is protected. We'll never share your info with anyone."}
                        wrapperText={
                            submitStatus === 'success'
                                ? (language === 'arabic' ? "تمت إضافتك إلى قائمة الانتظار. سيتواصل معك فريقنا شخصيًا فور توفر موعد." : "You've been added to our waitlist. Our team will reach out personally as soon as an appointment slot opens up.")
                                : submitStatus === 'error'
                                    ? (language === 'arabic' ? "فشل الإرسال. يرجى تحديث الصفحة والمحاولة مرة أخرى." : "Submission unsuccessful. Please refresh and try again.")
                                    : ''
                        }
                        wrapperBgColor={
                            submitStatus === 'success'
                                ? 'bg-greens-green-2'
                                : submitStatus === 'error'
                                    ? 'bg-state-error-base'
                                    : 'bg-greens-green-2'
                        }
                        isError={submitStatus === 'error'}
                    />
                </div>
            </div>
        </div>
    );
};

export default LPForm;
